"use client";

import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import type { SavedCity } from "@/components/choghadiya/ChoghadiyaCitySelector";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { getShubhMuhurat, type MuhuratPurpose, type ShubhMuhuratResponse } from "@/lib/api/muhurat";
import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";
import { todayISTDateString } from "./ShubhMuhuratDigest";
import { DEFAULT_SHUBH_CITY } from "./shubh-muhurat-config";
import {
  currentIndiaMinute,
  getCurrentMuhuratVerdict,
  needsPreviousNightDigest,
  previousCivilDate,
} from "./shubh-muhurat-verdict";

interface DigestPair {
  current: ShubhMuhuratResponse;
  previousNight: ShubhMuhuratResponse | null;
}

async function fetchCityDigest(
  city: SavedCity,
  date: string,
  purpose: MuhuratPurpose
): Promise<ShubhMuhuratResponse> {
  const digest = await getShubhMuhurat({
    date,
    latitude: city.latitude,
    longitude: city.longitude,
    timezone: city.timezone,
    purpose,
    place: city.name,
  });
  if (digest.date !== date) {
    throw new Error("Timing engine returned a digest for the wrong IST date");
  }
  if (digest.purpose !== purpose) {
    throw new Error("Timing engine returned a digest for the wrong purpose");
  }
  return digest;
}

async function fetchDigestPair(
  city: SavedCity,
  currentDate: string,
  purpose: MuhuratPurpose
): Promise<DigestPair> {
  const current = await fetchCityDigest(city, currentDate, purpose);
  if (!needsPreviousNightDigest(current, currentIndiaMinute())) {
    return { current, previousNight: null };
  }
  const previousNight = await fetchCityDigest(city, previousCivilDate(currentDate), purpose);
  return { current, previousNight };
}

function initialForDate(
  digest: ShubhMuhuratResponse | null | undefined,
  date: string
): ShubhMuhuratResponse | null {
  return digest?.date === date ? digest : null;
}

function needsPreviousContext(
  current: ShubhMuhuratResponse | null,
  previousNight: ShubhMuhuratResponse | null,
  nowMinute: number | null
): boolean {
  return Boolean(
    current && nowMinute !== null && needsPreviousNightDigest(current, nowMinute) && !previousNight
  );
}

function trackCompleted() {
  trackCalculatorUsed({ calculator: "shubh-muhurat", source_path: "/shubh-muhurat/" });
}

function useDigestLoader(
  currentInitialDigest: ShubhMuhuratResponse | null,
  currentInitialPreviousNight: ShubhMuhuratResponse | null
) {
  const [digest, setDigest] = useState<ShubhMuhuratResponse | null>(currentInitialDigest);
  const [previousNightDigest, setPreviousNightDigest] = useState<ShubhMuhuratResponse | null>(
    currentInitialPreviousNight
  );
  const [loading, setLoading] = useState(!currentInitialDigest);
  const [error, setError] = useState<string | null>(null);
  const loadedDate = useRef(currentInitialDigest?.date);
  const loadedPurpose = useRef<MuhuratPurpose | undefined>(currentInitialDigest?.purpose);
  const loadedPreviousDate = useRef(currentInitialPreviousNight?.date);
  const activeRequest = useRef(0);

  const load = useCallback(async (selectedCity: SavedCity, purpose: MuhuratPurpose) => {
    const requestId = ++activeRequest.current;
    const requestedDate = todayISTDateString();
    loadedDate.current = requestedDate;
    loadedPurpose.current = purpose;
    loadedPreviousDate.current = previousCivilDate(requestedDate);
    startTransition(() => {
      setLoading(true);
      setError(null);
    });
    try {
      const pair = await fetchDigestPair(selectedCity, requestedDate, purpose);
      if (requestId !== activeRequest.current) return;
      loadedPreviousDate.current = pair.previousNight?.date;
      startTransition(() => {
        setDigest(pair.current);
        setPreviousNightDigest(pair.previousNight);
      });
      trackCompleted();
    } catch (cause) {
      if (requestId !== activeRequest.current) return;
      reportJsError({
        ...jsErrorFieldsFromUnknown(cause),
        boundary: "shubh-muhurat-lookup",
      });
      startTransition(() => setError("The timing engine could not load. Please try again."));
    } finally {
      if (requestId === activeRequest.current) startTransition(() => setLoading(false));
    }
  }, []);

  return {
    digest,
    error,
    load,
    loadedDate,
    loadedPurpose,
    loadedPreviousDate,
    loading,
    previousNightDigest,
  };
}

function useIndiaClock(): number | null {
  const [nowMinute, setNowMinute] = useState<number | null>(null);
  useEffect(() => {
    const updateClock = () => setNowMinute(currentIndiaMinute());
    updateClock();
    const timer = window.setInterval(updateClock, 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return nowMinute;
}

interface RefreshInputs {
  city: SavedCity;
  currentInitialDigest: ShubhMuhuratResponse | null;
  currentInitialPreviousNight: ShubhMuhuratResponse | null;
  digest: ShubhMuhuratResponse | null;
  load: (city: SavedCity, purpose: MuhuratPurpose) => Promise<void>;
  loadedDate: { current: string | undefined };
  loadedPurpose: { current: MuhuratPurpose | undefined };
  loadedPreviousDate: { current: string | undefined };
  nowMinute: number | null;
  previousDate: string;
  previousNightDigest: ShubhMuhuratResponse | null;
  purpose: MuhuratPurpose;
  today: string;
}

function useDigestRefresh(inputs: RefreshInputs) {
  const trackedInitial = useRef(false);
  useEffect(() => {
    const initialContextMissing = needsPreviousContext(
      inputs.currentInitialDigest,
      inputs.currentInitialPreviousNight,
      inputs.nowMinute
    );
    if (
      inputs.currentInitialDigest &&
      inputs.nowMinute !== null &&
      !initialContextMissing &&
      !trackedInitial.current
    ) {
      trackedInitial.current = true;
      trackCompleted();
    }
    const previousContextMissing =
      needsPreviousContext(inputs.digest, inputs.previousNightDigest, inputs.nowMinute) &&
      inputs.loadedPreviousDate.current !== inputs.previousDate;
    const purposeChanged = inputs.loadedPurpose.current !== inputs.purpose;
    if (inputs.loadedDate.current !== inputs.today || previousContextMissing || purposeChanged) {
      void inputs.load(inputs.city, inputs.purpose);
    }
  }, [inputs]);
}

export function useShubhMuhurat(
  initialDigest: ShubhMuhuratResponse | null,
  initialPreviousNightDigest: ShubhMuhuratResponse | null
) {
  const today = todayISTDateString();
  const previousDate = previousCivilDate(today);
  const currentInitialDigest = initialForDate(initialDigest, today);
  const currentInitialPreviousNight = initialForDate(initialPreviousNightDigest, previousDate);
  const [city, setCity] = useState<SavedCity>(DEFAULT_SHUBH_CITY);
  const [purpose, setPurpose] = useState<MuhuratPurpose>("general");
  const nowMinute = useIndiaClock();
  const loader = useDigestLoader(currentInitialDigest, currentInitialPreviousNight);
  useDigestRefresh({
    city,
    currentInitialDigest,
    currentInitialPreviousNight,
    digest: loader.digest,
    load: loader.load,
    loadedDate: loader.loadedDate,
    loadedPurpose: loader.loadedPurpose,
    loadedPreviousDate: loader.loadedPreviousDate,
    nowMinute,
    previousDate,
    previousNightDigest: loader.previousNightDigest,
    purpose,
    today,
  });

  const selectCity = (selectedCity: SavedCity) => {
    setCity(selectedCity);
    void loader.load(selectedCity, purpose);
  };
  const selectPurpose = (selectedPurpose: MuhuratPurpose) => {
    setPurpose(selectedPurpose);
    void loader.load(city, selectedPurpose);
  };
  const verdict =
    loader.digest && nowMinute !== null
      ? getCurrentMuhuratVerdict(loader.digest, nowMinute, loader.previousNightDigest)
      : null;
  return {
    city,
    digest: loader.digest,
    error: loader.error,
    loading: loader.loading,
    purpose,
    retry: () => loader.load(city, purpose),
    selectCity,
    selectPurpose,
    verdict,
  };
}
